import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import {
  Users, Building2, CalendarDays, Zap, Search, Plus, X,
  ExternalLink, Sparkles, MapPin, Briefcase, MessageSquare,
} from "lucide-react";

const TYPE_META = {
  person: { label: "Personne", icon: Users, tone: "border-[#E4E1D8]" },
  organization: { label: "Organisation", icon: Building2, tone: "border-[#E4E1D8]" },
  event: { label: "Événement", icon: CalendarDays, tone: "border-[#E4E1D8]" },
  opportunity: { label: "Opportunité", icon: Zap, tone: "border-[#E7D9B3] bg-[#FBF6EB]" },
  signal: { label: "Signal faible", icon: Sparkles, tone: "border-[#E7D9B3] bg-[#FBF6EB]" },
};

export default function Network() {
  const [entities, setEntities] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [q, setQ] = useState("");
  const [joinOpen, setJoinOpen] = useState(false);
  const [followupTarget, setFollowupTarget] = useState(null);
  const [followupResult, setFollowupResult] = useState(null);
  const [followupNotes, setFollowupNotes] = useState("");
  const [followupLoading, setFollowupLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [st, en] = await Promise.all([
        api.get("/network/status"),
        api.get(`/network/ecosystem?limit=60${typeFilter ? `&type=${typeFilter}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
      ]);
      setStatus(st.data);
      setEntities(en.data.items || []);
    } catch (e) {
      const detail = e.response?.data?.detail || "Chargement impossible";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeFilter]);

  const grouped = useMemo(() => {
    const g = { person: [], organization: [], event: [], opportunity: [], signal: [] };
    entities.forEach((e) => { if (g[e.type]) g[e.type].push(e); });
    return g;
  }, [entities]);

  const openFollowup = (target) => {
    setFollowupTarget(target);
    setFollowupResult(null);
    setFollowupNotes("");
  };

  const runFollowup = async () => {
    if (!followupTarget) return;
    setFollowupLoading(true);
    try {
      const res = await api.post("/network/interactions/followup", {
        with_entity_id: followupTarget.id,
        notes_fr: followupNotes || null,
      });
      setFollowupResult(res.data);
    } catch (e) {
      toast.error("Génération impossible");
    } finally {
      setFollowupLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="network-page">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header */}
        <div className="flex items-start justify-between mb-10 gap-6 flex-wrap">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy mb-3">Network OS · Écosystème</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">Écosystème économique</h1>
            <p className="mt-5 text-base md:text-lg text-[#374151] leading-relaxed max-w-2xl">
              Personnes, organisations, événements et opportunités indexés autour de votre trajectoire.
              Un réseau d&apos;opportunités — jamais un réseau social.
            </p>
          </div>
          {status && !status.has_profile && (
            <button
              data-testid="network-join-btn"
              onClick={() => setJoinOpen(true)}
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy-700 text-white px-5 py-3 text-sm font-medium rounded-md shadow-sm transition-colors"
            >
              <Plus className="h-4 w-4" strokeWidth={2} /> Rejoindre le réseau
            </button>
          )}
        </div>

        {status?.founder_only && (
          <div className="mb-6 border-2 border-[#E7D9B3] bg-[#FBF6EB] rounded-lg px-5 py-4 text-sm text-[#7A5B14]">
            <span className="font-semibold">Phase pilote — </span>
            Les listings restent visibles aux membres fondateurs, mais les inscriptions professionnelles et partenaires sont ouvertes. Activation publique complète à venir.
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-8 flex-wrap" data-testid="network-filters">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5B5B62]" strokeWidth={2} />
            <input
              data-testid="network-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              placeholder="Chercher (nom, mot-clé, secteur)…"
              className="w-full border-2 border-[#E5E7EB] focus:border-navy rounded-md pl-10 pr-3 py-2.5 text-sm bg-white transition-colors outline-none"
            />
          </div>
          {["", "person", "organization", "event", "opportunity", "signal"].map((k) => (
            <button
              key={k || "all"}
              data-testid={`network-filter-${k || "all"}`}
              onClick={() => setTypeFilter(k)}
              className={`px-4 py-2 text-sm font-medium rounded-md border-2 transition-colors ${typeFilter === k ? "bg-navy text-white border-navy" : "bg-white text-[#374151] border-[#E5E7EB] hover:border-navy-200"}`}
            >
              {k ? TYPE_META[k]?.label : "Tous"}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-sm text-[#5B5B62]">Chargement…</div>
        ) : entities.length === 0 ? (
          <div className="border border-dashed border-[#E4E1D8] rounded-md p-8 text-center text-sm text-[#5B5B62]">
            Aucune entité pour ces critères.
          </div>
        ) : (
          <div className="space-y-8">
            {["person", "organization", "event", "opportunity", "signal"].map((t) => (
              grouped[t].length > 0 && (
                <section key={t} data-testid={`network-section-${t}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {(() => { const I = TYPE_META[t].icon; return <I className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />; })()}
                    <h2 className="text-sm font-mono uppercase tracking-[0.18em] text-[#5B5B62]">{TYPE_META[t].label}s <span className="text-[#8B8B8F]">({grouped[t].length})</span></h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {grouped[t].map((e) => (
                      <EntityCard key={e.id} entity={e} onFollowup={openFollowup} />
                    ))}
                  </div>
                </section>
              )
            ))}
          </div>
        )}
      </div>

      {joinOpen && <JoinModal onClose={() => setJoinOpen(false)} onDone={() => { setJoinOpen(false); load(); toast.success("Bienvenue dans le réseau !"); }} />}

      {followupTarget && (
        <FollowupModal
          target={followupTarget}
          notes={followupNotes}
          setNotes={setFollowupNotes}
          loading={followupLoading}
          result={followupResult}
          onRun={runFollowup}
          onClose={() => { setFollowupTarget(null); setFollowupResult(null); }}
        />
      )}
    </div>
  );
}

function EntityCard({ entity, onFollowup }) {
  const meta = TYPE_META[entity.type] || TYPE_META.person;
  const Icon = meta.icon;
  return (
    <div className={`border ${meta.tone} bg-white rounded-md p-4`} data-testid={`network-entity-${entity.id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.5} />
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#5B5B62]">{meta.label}</span>
        {entity.is_demo && (
          <span
            data-testid={`network-entity-demo-${entity.id}`}
            title="Persona de démonstration — sera remplacée par de vrais membres inscrits"
            className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 rounded-sm"
          >
            Démo
          </span>
        )}
        {entity.urgency && entity.urgency !== "medium" && (
          <span className={`text-[10px] font-mono px-1.5 py-0.5 border rounded-sm ${entity.urgency === "high" || entity.urgency === "critical" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
            {entity.urgency}
          </span>
        )}
      </div>
      <div className="text-sm font-medium text-[#1F2937] leading-snug mb-1">{entity.name_fr}</div>
      {entity.description_fr && <div className="text-xs text-[#5B5B62] line-clamp-3">{entity.description_fr}</div>}
      <div className="mt-2 flex items-center gap-3 text-[11px] font-mono text-[#8B8B8F]">
        {entity.sectors?.slice(0, 2).map((s) => <span key={s} className="inline-flex items-center gap-1"><Briefcase className="h-3 w-3" /> {s}</span>)}
        {entity.territories?.slice(0, 1).map((t) => <span key={t} className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {t}</span>)}
      </div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {entity.url && (
          <a href={entity.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs border border-[#E4E1D8] px-2 py-1 rounded-md text-[#0B2545] hover:bg-[#F1EFE9]">
            <ExternalLink className="h-3 w-3" /> Site
          </a>
        )}
        <button
          data-testid={`network-followup-${entity.id}`}
          onClick={() => onFollowup(entity)}
          className="inline-flex items-center gap-1 text-xs border border-[#E4E1D8] px-2 py-1 rounded-md text-[#0B2545] hover:bg-[#F1EFE9]"
        >
          <MessageSquare className="h-3 w-3" /> Après rencontre
        </button>
      </div>
    </div>
  );
}

function JoinModal({ onClose, onDone }) {
  const [form, setForm] = useState({
    display_name: "",
    role_network: "professionnel",
    headline_fr: "",
    bio_fr: "",
    expertises: "",
    sectors: "",
    languages: "fr",
    city: "",
    country: "Canada",
    availability: "medium",
    open_to: [],
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.display_name || !form.headline_fr) { toast.error("Nom et intitulé requis"); return; }
    setSubmitting(true);
    try {
      await api.post("/network/join", {
        ...form,
        expertises: form.expertises.split(",").map((s) => s.trim()).filter(Boolean),
        sectors: form.sectors.split(",").map((s) => s.trim()).filter(Boolean),
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      });
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Adhésion échouée");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="network-join-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-display text-[#0B2545]">Rejoindre le réseau</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-[#5B5B62]" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Nom d'affichage *">
            <input data-testid="join-display-name" required value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" />
          </Field>
          <Field label="Intitulé professionnel *">
            <input data-testid="join-headline" required value={form.headline_fr} onChange={(e) => setForm({ ...form, headline_fr: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" placeholder="Ex: Analyste SOC en reconversion IA" />
          </Field>
          <Field label="Rôle dans le réseau">
            <select data-testid="join-role" value={form.role_network} onChange={(e) => setForm({ ...form, role_network: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm">
              <option value="professionnel">Professionnel</option>
              <option value="mentor">Mentor</option>
              <option value="pair">Pair</option>
              <option value="recruteur">Recruteur</option>
              <option value="expert">Expert</option>
              <option value="entrepreneur">Entrepreneur</option>
              <option value="investisseur">Investisseur</option>
              <option value="ancien_etudiant">Ancien étudiant</option>
              <option value="leader_communautaire">Leader communautaire</option>
              <option value="conseiller">Conseiller</option>
            </select>
          </Field>
          <Field label="Bio (courte)">
            <textarea value={form.bio_fr} onChange={(e) => setForm({ ...form, bio_fr: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" rows="2" />
          </Field>
          <Field label="Expertises (séparées par virgule)">
            <input value={form.expertises} onChange={(e) => setForm({ ...form, expertises: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" placeholder="SIEM, EDR, Python" />
          </Field>
          <Field label="Secteurs">
            <input value={form.sectors} onChange={(e) => setForm({ ...form, sectors: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" placeholder="cybersecurite, technologie" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Ville"><input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" /></Field>
            <Field label="Pays"><input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="border border-[#E4E1D8] px-4 py-2 text-sm rounded-md">Annuler</button>
            <button data-testid="join-submit" type="submit" disabled={submitting} className="bg-[#0B2545] hover:bg-[#123163] text-white px-4 py-2 text-sm rounded-md disabled:opacity-50">
              {submitting ? "Envoi…" : "Rejoindre"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FollowupModal({ target, notes, setNotes, loading, result, onRun, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="followup-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#5B5B62]">Assistant post-rencontre</div>
            <h3 className="text-lg font-display text-[#0B2545]">{target.name_fr}</h3>
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-[#5B5B62]" /></button>
        </div>
        {!result && (
          <>
            <Field label="Notes (facultatif)">
              <textarea data-testid="followup-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm" rows="4" placeholder="Ce qui a été discuté, l'énergie, les besoins…" />
            </Field>
            <div className="flex justify-end mt-3">
              <button data-testid="followup-run" onClick={onRun} disabled={loading} className="bg-[#0B2545] hover:bg-[#123163] text-white px-4 py-2 text-sm rounded-md disabled:opacity-50">
                {loading ? "Génération…" : "Générer résumé + courriel"}
              </button>
            </div>
          </>
        )}
        {result && (
          <div className="space-y-4" data-testid="followup-result">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62] mb-1">Résumé</div>
              <div className="text-sm text-[#1F2937] bg-[#F8F7F3] border border-[#E4E1D8] rounded-md p-3">{result.summary_fr}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62] mb-1">Courriel de suivi</div>
              <textarea readOnly value={result.followup_email_fr} className="w-full border border-[#E4E1D8] rounded-md px-3 py-2 text-sm bg-[#F8F7F3]" rows="6" />
            </div>
            {result.next_steps_fr?.length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62] mb-1">Prochaines étapes</div>
                <ul className="text-sm text-[#1F2937] list-disc list-inside space-y-1">
                  {result.next_steps_fr.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
            )}
            <div className="text-[11px] font-mono text-[#8B8B8F]">Rappel dans {result.reminder_days} jours · {result.llm_used}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[#5B5B62] mb-1">{label}</div>
      {children}
    </label>
  );
}
